import { Field, Formik } from 'formik';
import React, { useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import { Button, Fieldset, Input, Select } from '@fourthwall/components';
import { CloseIcon, TickIcon } from '@fourthwall/icons';
import { getFormValues } from '../../../utils/FormValues';
import * as S from './InlineSettingEditor.styled';
import { CheckboxForm } from './forms/CheckboxForm';
import { ColorForm } from './forms/ColorForm';
import { HeaderForm } from './forms/HeaderForm';
import { NumberForm } from './forms/NumberForm';
import { RadioForm } from './forms/RadioForm';
import { RangeForm } from './forms/RangeForm';
import { SelectForm } from './forms/SelectForm';
import { TextForm } from './forms/TextForm';
const SETTING_TYPES = {
    header: { label: 'Header' },
    text: { label: 'Text Input' },
    number: { label: 'Number Input' },
    color: { label: 'Color Picker' },
    checkbox: { label: 'Checkbox' },
    radio: { label: 'Radio Buttons' },
    select: { label: 'Select Dropdown' },
    range: { label: 'Range Slider' },
};
const validationSchema = Yup.object().shape({
    type: Yup.string().required('Setting type is required'),
    id: Yup.string().when('type', {
        is: (type) => type !== 'header',
        then: (schema) => schema.required('Setting ID is required'),
        otherwise: (schema) => schema.optional(),
    }),
    // optionals
    content: Yup.string().when('type', {
        is: 'header',
        then: (schema) => schema.required('Content is required for headers'),
        otherwise: (schema) => schema.optional(),
    }),
    label: Yup.string().when('type', {
        is: (type) => type !== 'header',
        then: (schema) => schema.required('Label is required'),
        otherwise: (schema) => schema.optional(),
    }),
    options: Yup.array().of(Yup.object().shape({
        value: Yup.string().required('Value is required'),
        label: Yup.string().required('Label is required'),
    })),
});
const FormInner = ({ values, setFieldValue, errors, touched, create }) => {
    const renderForm = () => {
        switch (values.type) {
            case 'color':
                return React.createElement(ColorForm, { setFieldValue: setFieldValue, values: values });
            case 'header':
                return React.createElement(HeaderForm, null);
            case 'text':
                return React.createElement(TextForm, null);
            case 'number':
                return React.createElement(NumberForm, null);
            case 'range':
                return React.createElement(RangeForm, null);
            case 'radio':
                return React.createElement(RadioForm, { setFieldValue: setFieldValue, values: values });
            case 'select':
                return React.createElement(SelectForm, { setFieldValue: setFieldValue, values: values });
            case 'checkbox':
                return React.createElement(CheckboxForm, null);
            default:
                return null;
        }
    };
    const renderTypeSelector = () => {
        return (React.createElement(Fieldset.Row, null,
            React.createElement(Fieldset.Item, null,
                React.createElement(Select, { label: "Setting Type", options: Object.entries(SETTING_TYPES).map(([type, { label }]) => ({
                        value: type,
                        label,
                    })), value: values.type, onValueChange: (value) => setFieldValue('type', value), disabled: !create }))));
    };
    const renderIdField = () => {
        return (React.createElement(Fieldset.Row, null,
            React.createElement(Fieldset.Item, null,
                React.createElement(Field, { name: "id", component: Input, label: "Setting ID", error: touched.id && errors.id ? errors.id : undefined, disabled: !create }))));
    };
    return (React.createElement(S.EditorContent, null,
        React.createElement(Fieldset, null,
            renderTypeSelector(),
            values.type !== 'header' && renderIdField(),
            renderForm())));
};
export const InlineSettingEditor = ({ setting, onSave, onClose, icon, header, create, }) => {
    const parentRef = useRef(null);
    const initialValues = getFormValues(setting);
    const handleSubmit = (values) => {
        const newSetting = {
            ...values,
            id: values.type === 'header' ? uuidv4() : values.id,
        };
        onSave(newSetting);
    };
    return (React.createElement(Formik, { initialValues: initialValues, onSubmit: handleSubmit, enableReinitialize: true, validationSchema: validationSchema }, ({ values, setFieldValue, submitForm, errors, touched }) => (React.createElement(S.SettingItem, null,
        React.createElement(S.SettingItemHeader, null,
            React.createElement(S.EditHandle, null, icon),
            header,
            React.createElement(S.ButtonContainer, null,
                React.createElement(Button, { appearance: "destructive-inverted", size: "small", leftIcon: React.createElement(CloseIcon, null), onClick: onClose }),
                React.createElement(Button, { appearance: "primary", size: "small", leftIcon: React.createElement(TickIcon, { height: 12 }), onClick: submitForm }))),
        React.createElement(S.EditingContainer, { ref: parentRef },
            React.createElement(FormInner, { values: values, setFieldValue: setFieldValue, errors: errors, touched: touched, create: create }))))));
};
